import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import { CalendarCheck, MapPin } from "lucide-react";
import { useBrand } from "../context/BrandContext";
import { usePlayerPresencePrompt } from "../hooks/usePlayerPresencePrompt";
import {
  parseSessionWindow,
  sessionDateKey,
} from "../utils/liveTrainingSessionWindow";
import "./PlayerPresenceConfirmDialog.css";

function formatWhen(session, locale, t) {
  if (!session?.ts_date) return "";
  const sessionKey = sessionDateKey(session);
  const todayKey = sessionDateKey({ ts_date: new Date() });
  const when =
    sessionKey && sessionKey === todayKey
      ? t("playerPresencePromptToday")
      : t("playerPresencePromptTomorrow");
  const w = parseSessionWindow(session);
  const start = w ? new Date(w.startMs) : new Date(session.ts_date);
  const time = w
    ? start.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      })
    : String(session.ts_start_time || "").slice(0, 5);
  const dateLabel = start.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "short",
  });
  return { when, time, dateLabel };
}

export function PlayerPresenceConfirmDialog() {
  const { t, i18n } = useTranslation();
  const { themeMode } = useBrand();
  const prompt = usePlayerPresencePrompt();
  const session = prompt.session;
  const meta = session ? formatWhen(session, i18n.language, t) : null;
  const groupName = session?.gr_name || t("playerSessionGroup");
  const errorText =
    prompt.error === "emailVerificationRequiredBody"
      ? t("emailVerificationRequiredBody")
      : prompt.error
        ? t(prompt.error, prompt.error)
        : "";

  return (
    <Dialog
      open={prompt.open}
      onClose={prompt.submitting ? undefined : prompt.later}
      fullWidth
      maxWidth="sm"
      className={themeMode === "light" ? "light-mode" : "dark-mode"}
      PaperProps={{
        className: `ppc-dialog ppc-dialog--${themeMode === "light" ? "light" : "dark"}`,
        "aria-labelledby": "ppc-title",
      }}
    >
      <div className="ppc">
        <div className="ppc__body">
          <div className="ppc__hero" aria-hidden>
            <CalendarCheck size={26} strokeWidth={2.1} />
          </div>
          <h2 id="ppc-title" className="ppc__title">
            {t("playerPresencePromptTitle", { groupName })}
          </h2>
          <p className="ppc__lead">
            {t("playerPresencePromptLead", { groupName })}
          </p>

          {session ? (
            <div className="ppc__card">
              <p className="ppc__when">{meta.when}</p>
              <p className="ppc__session">
                {groupName}
                {session.ts_session_number != null
                  ? ` · #${session.ts_session_number}`
                  : ""}
              </p>
              <p className="ppc__meta">
                <MapPin size={14} strokeWidth={2.2} />
                {meta.dateLabel}
                {meta.time ? ` · ${meta.time}` : ""}
                {session.ts_duration ? ` · ${session.ts_duration} ${t("min")}` : ""}
              </p>
            </div>
          ) : null}

          <p className="ppc__hint">{t("playerPresencePromptSeatHint")}</p>

          {errorText ? (
            <p className="ppc__error" role="alert">
              {errorText}
            </p>
          ) : null}
        </div>

        <div className="ppc__actions">
          <button
            type="button"
            className="ppc__btn ppc__btn--yes"
            onClick={prompt.confirm}
            disabled={prompt.submitting}
          >
            {prompt.submitting ? t("saving") || t("starting") : t("playerPresencePromptYes")}
          </button>
          <button
            type="button"
            className="ppc__btn ppc__btn--no"
            onClick={prompt.decline}
            disabled={prompt.submitting}
          >
            {t("playerPresencePromptNo")}
          </button>
          <button
            type="button"
            className="ppc__btn ppc__btn--later"
            onClick={prompt.later}
            disabled={prompt.submitting}
          >
            {t("playerPresencePromptLater")}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

export default PlayerPresenceConfirmDialog;
